import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { bulkJobsAPI, BulkJob, BulkJobItem, BulkItemStatus, TagEntreesDecision } from '../../services/bulkJobsAPI';
import { LOADING_STATES } from '../../constants';
import type { RootState } from '../../app/store';

// ─── Types ────────────────────────────────────────────────────────────

export interface BulkJobsState {
    /** Lot actuellement ouvert dans le panneau de revue */
    activeJobId: string | null;
    job: BulkJob | null;
    items: BulkJobItem[];
    itemsTotal: number;
    status: string; // LOADING_STATES
    error: string | null;
}

/** Statuts d'item que la revue peut basculer (miroir du filtre serveur `setItemsStatus`) */
const REVIEWABLE_ITEM_STATUSES: BulkItemStatus[] = ['generated', 'accepted', 'rejected'];

const initialState: BulkJobsState = {
    activeJobId: null,
    job: null,
    items: [],
    itemsTotal: 0,
    status: LOADING_STATES.IDLE,
    error: null,
};

// ─── Async Thunks ─────────────────────────────────────────────────────

/** Récupère un lot et ses items (paginés) */
export const fetchBulkJob = createAsyncThunk<
    { job: BulkJob; items: BulkJobItem[]; itemsTotal: number },
    { jobId: string; accessToken: string; itemsOffset?: number; itemsLimit?: number },
    { state: RootState; rejectValue: string }
>(
    'bulkJobs/fetchBulkJob',
    async ({ jobId, accessToken, itemsOffset, itemsLimit }, { rejectWithValue }) => {
        try {
            return await bulkJobsAPI.get(jobId, accessToken, itemsOffset, itemsLimit);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur de chargement du lot');
        }
    }
);

/** Avance le traitement d'un lot (génération/application) */
export const advanceBulkJob = createAsyncThunk<
    { job: BulkJob; processed: number },
    { jobId: string; accessToken: string },
    { state: RootState; rejectValue: string }
>(
    'bulkJobs/advanceBulkJob',
    async ({ jobId, accessToken }, { rejectWithValue }) => {
        try {
            return await bulkJobsAPI.advance(jobId, accessToken);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur d\'avancement du lot');
        }
    }
);

/** Met à jour le statut d'items (accepted/rejected) */
export const updateBulkItems = createAsyncThunk<
    { modified: number },
    { jobId: string; accessToken: string; itemIds?: string[]; all?: boolean; status: 'accepted' | 'rejected'; decision?: TagEntreesDecision },
    { state: RootState; rejectValue: string }
>(
    'bulkJobs/updateBulkItems',
    async ({ jobId, accessToken, itemIds, all, status, decision }, { rejectWithValue }) => {
        try {
            return await bulkJobsAPI.updateItems(jobId, { itemIds, all, status, decision }, accessToken);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur de mise à jour des items');
        }
    }
);

/** Démarre l'application d'un lot en revue */
export const applyBulkJob = createAsyncThunk<
    { job: BulkJob },
    { jobId: string; accessToken: string },
    { state: RootState; rejectValue: string }
>(
    'bulkJobs/applyBulkJob',
    async ({ jobId, accessToken }, { rejectWithValue }) => {
        try {
            return await bulkJobsAPI.apply(jobId, accessToken);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur d\'application du lot');
        }
    }
);

/** Annule un lot encore modifiable */
export const cancelBulkJob = createAsyncThunk<
    { job: BulkJob },
    { jobId: string; accessToken: string },
    { state: RootState; rejectValue: string }
>(
    'bulkJobs/cancelBulkJob',
    async ({ jobId, accessToken }, { rejectWithValue }) => {
        try {
            return await bulkJobsAPI.cancel(jobId, accessToken);
        } catch (error: any) {
            return rejectWithValue(error.message || 'Erreur d\'annulation du lot');
        }
    }
);

// ─── Slice ────────────────────────────────────────────────────────────

/**
 * Réponse d'un lot qui n'est plus celui de la revue : fermée (ou passée à un autre lot)
 * pendant la requête. Sans cette garde, la réponse réinstallait le lot fermé dans le store
 * (« lot fantôme ») et la revue suivante démarrait sur le statut d'un autre lot.
 */
const isStale = (state: BulkJobsState, jobId: string | undefined): boolean =>
    jobId !== undefined && jobId !== state.activeJobId;

/** Lot annulé : une réponse partie avant l'annulation ne doit pas le remettre en génération. */
const keepsCancelled = (state: BulkJobsState, job: BulkJob | null | undefined): boolean =>
    Boolean(job) && state.job?._id === job?._id && state.job?.status === 'cancelled' && job?.status !== 'cancelled';

const bulkJobsSlice = createSlice({
    name: 'bulkJobs',
    initialState,
    reducers: {
        /** Ouvre le panneau de revue sur un lot donné */
        openBulkReview(state, action: PayloadAction<string>) {
            state.activeJobId = action.payload;
        },
        /** Ferme le panneau de revue et réinitialise le lot actif */
        closeBulkReview(state) {
            state.activeJobId = null;
            state.job = null;
            state.items = [];
            state.itemsTotal = 0;
            state.status = LOADING_STATES.IDLE;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ── fetchBulkJob ──
            .addCase(fetchBulkJob.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
                state.error = null;
            })
            .addCase(fetchBulkJob.fulfilled, (state, action) => {
                if (isStale(state, action.meta.arg.jobId)) return;
                state.status = LOADING_STATES.SUCCEEDED;
                if (keepsCancelled(state, action.payload.job)) return;
                state.job = action.payload.job;
                // Pagination : un fetch avec offset AJOUTE à la liste (« Charger la
                // suite ») ; sans offset, il la remplace (chargement initial, refresh).
                const offset = action.meta.arg.itemsOffset ?? 0;
                if (offset > 0) {
                    const known = new Set(state.items.map(i => i._id));
                    state.items = [...state.items, ...action.payload.items.filter(i => !known.has(i._id))];
                } else {
                    state.items = action.payload.items;
                }
                state.itemsTotal = action.payload.itemsTotal;
            })
            .addCase(fetchBulkJob.rejected, (state, action) => {
                if (isStale(state, action.meta?.arg?.jobId)) return;
                state.status = LOADING_STATES.FAILED;
                state.error = action.payload || action.error.message || 'Erreur inconnue';
            })

            // ── advanceBulkJob ──
            .addCase(advanceBulkJob.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
                state.error = null;
            })
            .addCase(advanceBulkJob.fulfilled, (state, action) => {
                if (isStale(state, action.meta.arg.jobId)) return;
                state.status = LOADING_STATES.SUCCEEDED;
                if (keepsCancelled(state, action.payload.job)) return;
                state.job = action.payload.job;
            })
            .addCase(advanceBulkJob.rejected, (state, action) => {
                if (isStale(state, action.meta?.arg?.jobId)) return;
                state.status = LOADING_STATES.FAILED;
                state.error = action.payload || action.error.message || 'Erreur inconnue';
            })

            // ── updateBulkItems ──
            .addCase(updateBulkItems.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
                state.error = null;
            })
            .addCase(updateBulkItems.fulfilled, (state, action) => {
                state.status = LOADING_STATES.SUCCEEDED;
                // Report optimiste du statut serveur sur les items en mémoire. Sans lui,
                // « Tout accepter » ne changeait rien à l'écran : les puces restaient
                // « Généré » et « Appliquer » restait désactivé (acceptedCount === 0),
                // rendant la revue impossible à conclure.
                const { all, itemIds, status, decision } = action.meta.arg;
                const targeted = new Set(itemIds ?? []);
                for (const item of state.items) {
                    // Mêmes items que le serveur bascule (BulkJobService.setItemsStatus)
                    if (!REVIEWABLE_ITEM_STATUSES.includes(item.status)) continue;
                    if (all || targeted.has(item._id)) item.status = status;
                }
                // Décision (design § 3.2) : le serveur ne l'accepte que pour un accept ciblant
                // UN item ; l'absence de décision efface celle mémorisée. « Tout accepter »
                // (`all: true`) ne touche jamais aux décisions existantes.
                if (!all && status === 'accepted' && itemIds?.length === 1) {
                    const item = state.items.find(i => i._id === itemIds[0]);
                    if (item) {
                        if (decision) item.decision = decision;
                        else delete item.decision;
                    }
                }
            })
            .addCase(updateBulkItems.rejected, (state, action) => {
                state.status = LOADING_STATES.FAILED;
                state.error = action.payload || action.error.message || 'Erreur inconnue';
            })

            // ── applyBulkJob ──
            .addCase(applyBulkJob.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
                state.error = null;
            })
            .addCase(applyBulkJob.fulfilled, (state, action) => {
                state.status = LOADING_STATES.SUCCEEDED;
                state.job = action.payload.job;
            })
            .addCase(applyBulkJob.rejected, (state, action) => {
                state.status = LOADING_STATES.FAILED;
                state.error = action.payload || action.error.message || 'Erreur inconnue';
            })

            // ── cancelBulkJob ──
            .addCase(cancelBulkJob.pending, (state) => {
                state.status = LOADING_STATES.LOADING;
                state.error = null;
            })
            .addCase(cancelBulkJob.fulfilled, (state, action) => {
                state.status = LOADING_STATES.SUCCEEDED;
                state.job = action.payload.job;
            })
            .addCase(cancelBulkJob.rejected, (state, action) => {
                state.status = LOADING_STATES.FAILED;
                state.error = action.payload || action.error.message || 'Erreur inconnue';
            });
    },
});

export const { openBulkReview, closeBulkReview } = bulkJobsSlice.actions;

// ─── Selectors ────────────────────────────────────────────────────────

export const selectBulkActiveJobId = (state: RootState) => state.bulkJobs.activeJobId;
export const selectBulkJob = (state: RootState) => state.bulkJobs.job;
export const selectBulkItems = (state: RootState) => state.bulkJobs.items;
export const selectBulkStatus = (state: RootState) => state.bulkJobs.status;

export default bulkJobsSlice.reducer;
